import React, { useEffect } from "react";
import {
  Navigate,
  useNavigate,
  Outlet,
  useSearchParams,
} from "react-router-dom";
import { routesMap, routeToViewName } from "@/components/routes/routes";
import localStoreService from "@/services/middleware/storageService";
import { getPermissions } from "@/services/service-layer/permissionsService";
import useUser from "@/components/hooks/use-user/use-user";
import useAccountFeatures from "@/components/hooks/use-account-features/use-account-features";
import useBaseRoute from "@/components/hooks/use-base-route/use-base-route";
import storageService from "@/services/middleware/storageService";
import { usePermissionsStore } from "@/zustand/permissionsStore";
import useRelationships from "@/components/hooks/use-relationships/use-relationships";
import { useAuthStore } from "@/zustand/authStore";
import AppSumoAuth from "@/pages/auth/views/AppSumoAuth";
import { userIsSelfService } from "@/utilities/planHelpers";
import { FT_NAME } from "@/components/routes/feature-access/featureAccessResolver";

/**
 * 1. Is auth? -> /auth/login
 * 2. Has role? -> /role
 * 3. Has user data? -> /loader
 *
 * @returns {*}
 * @constructor
 */

const PrivateRoute = () => {
  const [searchParams] = useSearchParams();
  const authToken = searchParams.get("auth");
  const isAuthenticated = Boolean(storageService.getItem("token"));
  const { user, trialExpiresAt, sensorIntegrated, deactivatedAt } = useUser();
  const { permissions: userPermissions } = usePermissionsStore(
    (state) => state
  );
  const { setLogout } = useAuthStore((state) => state);
  const navigate = useNavigate();
  const expiresDate = new Date(trialExpiresAt);
  const now = new Date();
  const isRelationshipPickerForced: boolean = new URL(
    window.location.href
  ).searchParams.has("forceSelection");
  const activeRelationship: string | null = isRelationshipPickerForced
    ? null
    : localStoreService.getItem("activeRelationshipId");
  const currentView = routeToViewName[window.location.pathname];
  const { isFeatureVisible, getFeatureAccess } = useAccountFeatures();
  const hasFirewallPermission = isFeatureVisible(FT_NAME.SENSOR_FIREWALL);
  const { baseRoute } = useBaseRoute({
    isOverviewVisible: isFeatureVisible(FT_NAME.OVERVIEW) || false,
  });
  const isGhost = storageService.getGhost() === true;
  const isDeactivatedMoreThan30Days =
    deactivatedAt &&
    now.getTime() - new Date(deactivatedAt).getTime() >
      30 * 24 * 60 * 60 * 1000;
  const redirectToSubscriptionCenter =
    isDeactivatedMoreThan30Days && isGhost && userIsSelfService(user);

  useEffect(() => {
    const userPermissionsKeys = Object.keys(userPermissions);
    if (!userPermissionsKeys.length) {
      getPermissions();
    }
  }, []);

  const { activeRelationship: storedActiveRelationship } = useRelationships();

  // base redirect enforces the base route to be set correctly
  // when there is no sensor integrated
  useEffect(() => {
    if (!hasFirewallPermission && sensorIntegrated === false) {
      navigate(routesMap.setup.setupCenter.path);
    }
  }, [sensorIntegrated]);

  // Handle all navigation logic in useEffect to avoid setState during render
  useEffect(() => {
    if (
      !isAuthenticated ||
      Object.keys(user).length === 0 ||
      !activeRelationship
    ) {
      return; // These cases are handled by early returns in render
    }

    // Avoids router get lost on root loading
    if (!currentView) {
      navigate(baseRoute);
      return;
    }

    if (
      storedActiveRelationship?.active === false &&
      storedActiveRelationship?.planMode === null
    ) {
      if (
        "paymentUrl" in storedActiveRelationship &&
        storedActiveRelationship.paymentUrl !== null
      ) {
        setLogout();
        navigate(routesMap.accountInactive.path);
        return;
      } else {
        navigate(routesMap.accountMissing.path);
        return;
      }
    }
    const access = getFeatureAccess(currentView);

    if (access?.visibility === "hidden") {
      navigate(baseRoute);
      return;
    }

    if (
      (userIsSelfService(user) &&
        storedActiveRelationship?.active === false &&
        now > expiresDate &&
        !window.location.href.includes("subscription-center")) ||
      (redirectToSubscriptionCenter &&
        !window.location.href.includes("subscription-center"))
    ) {
      navigate(routesMap.account.subscriptionCenter.path);
      return;
    }
  }, [
    isAuthenticated,
    user,
    activeRelationship,
    currentView,
    baseRoute,
    storedActiveRelationship,
    isFeatureVisible,
    setLogout,
    navigate,
  ]);

  if (authToken) {
    return <AppSumoAuth />;
  }
  if (!isAuthenticated) {
    return <Navigate to={routesMap.auth.login.path} />;
  }

  if (!activeRelationship) {
    return <Navigate to={routesMap.role.path} />;
  }

  return <Outlet />;
};

export default React.memo(PrivateRoute);
